import { mobileApi } from '@/lib/mobileApi';
import type {
  ClienteDashboard,
  ClienteMobileUser,
  ClienteTitulo,
  ClienteApiNotification,
  MinhaIndicacao,
} from '@/types/clienteMobile';

function unwrapData<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'data' in (raw as object)) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

export const clienteMobileService = {
  async getZehMotocaInstagramFeed(): Promise<Array<{
    id: string;
    type: 'reel' | 'post';
    permalink: string;
    caption: string | null;
    thumbnail: string | null;
    publishedAt: string | null;
  }>> {
    const res = await mobileApi.get('/zehmotoca/instagram-feed');
    const inner = unwrapData<Array<{
      id: string;
      type: 'reel' | 'post';
      permalink: string;
      caption: string | null;
      thumbnail: string | null;
      publishedAt: string | null;
    }>>(res.data);
    return Array.isArray(inner) ? inner : [];
  },

  async login(email: string, password: string): Promise<{
    user: ClienteMobileUser;
    accessToken: string;
    refreshToken: string;
  }> {
    const res = await mobileApi.post('/auth/login', { email: email.trim(), password });
    const payload = unwrapData<{ user: ClienteMobileUser; accessToken: string; refreshToken: string }>(res.data);
    return payload;
  },

  async getProfile(): Promise<{ user: ClienteMobileUser }> {
    const res = await mobileApi.get('/auth/profile');
    return unwrapData<{ user: ClienteMobileUser }>(res.data);
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await mobileApi.post('/auth/change-password', { currentPassword, newPassword });
  },

  async getDashboard(): Promise<ClienteDashboard> {
    const res = await mobileApi.get('/dashboard');
    return unwrapData<ClienteDashboard>(res.data);
  },

  async getTitulos(filters?: { tipo?: 'vencido' | 'a_vencer' | 'pago' }): Promise<ClienteTitulo[]> {
    const params = new URLSearchParams();
    if (filters?.tipo) params.set('tipo', filters.tipo);
    const q = params.toString();
    const res = await mobileApi.get(`/titulos${q ? `?${q}` : ''}`);
    const inner = unwrapData<{ titulos: ClienteTitulo[] }>(res.data);
    return inner.titulos ?? [];
  },

  async getMultas(filters?: { tipo?: 'vencida' | 'a_vencer' | 'paga' }): Promise<ClienteTitulo[]> {
    const params = new URLSearchParams();
    if (filters?.tipo) params.set('tipo', filters.tipo);
    const q = params.toString();
    const res = await mobileApi.get(`/multas${q ? `?${q}` : ''}`);
    const inner = unwrapData<{ multas: ClienteTitulo[] }>(res.data);
    return inner.multas ?? [];
  },

  async getCentraisAtendimento(): Promise<{
    phoneEmergency: string | null;
    phoneWorkshop: string | null;
    phoneFinancial: string | null;
    phoneCustomerService: string | null;
    phoneTowRequest: string | null;
    phoneSos: string | null;
  }> {
    try {
      const res = await mobileApi.get('/centrais-atendimento');
      const d = unwrapData<Record<string, string | null>>(res.data);
      return {
        phoneEmergency: d.phoneEmergency ?? null,
        phoneWorkshop: d.phoneWorkshop ?? null,
        phoneFinancial: d.phoneFinancial ?? null,
        phoneCustomerService: d.phoneCustomerService ?? null,
        phoneTowRequest: d.phoneTowRequest ?? null,
        phoneSos: d.phoneSos ?? null,
      };
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        return {
          phoneEmergency: null,
          phoneWorkshop: null,
          phoneFinancial: null,
          phoneCustomerService: null,
          phoneTowRequest: null,
          phoneSos: null,
        };
      }
      throw e;
    }
  },

  async getIndicacaoLink(): Promise<{ url: string; message?: string }> {
    const res = await mobileApi.get('/indicacao/link');
    return unwrapData<{ url: string; message?: string }>(res.data);
  },

  async getMinhasIndicacoes(): Promise<MinhaIndicacao[]> {
    const res = await mobileApi.get('/minhas-indicacoes');
    const inner = unwrapData<MinhaIndicacao[] | { indicacoes?: MinhaIndicacao[] }>(res.data);
    if (Array.isArray(inner)) return inner;
    return inner.indicacoes ?? [];
  },

  async getNotifications(limit = 50): Promise<ClienteApiNotification[]> {
    const res = await mobileApi.get(`/notifications?limit=${limit}`);
    const inner = unwrapData<{ notifications: ClienteApiNotification[] }>(res.data);
    return inner.notifications ?? [];
  },

  async getUnreadCount(): Promise<number> {
    const res = await mobileApi.get('/notifications/unread-count');
    const inner = unwrapData<{ count: number }>(res.data);
    return inner.count ?? 0;
  },

  async markNotificationRead(id: string): Promise<void> {
    await mobileApi.patch(`/notifications/${id}/read`);
  },

  async markAllNotificationsRead(): Promise<void> {
    await mobileApi.patch('/notifications/mark-all-read');
  },
};
